import React, { useMemo, useState, useRef } from "react";
import { Checkbox } from "antd";
import CustomAvatar from "../../../Core/CommonV2/CustomAvatar";
import CustomModal from "../../../Core/CommonV2/CustomModal";
import CustomSearchBox from "../../../Core/CommonV2/CustomSearchBox";
import CustomButton from "../../../Core/CommonV2/CustomButton";
import useSearch from "../../../Core/hooks/useSearch";
import { useAppDispatch, useAppSelector } from "../../../Core/redux/hooks";
import {
  resetConversations,
  updateConversationsMultiple,
} from "../../../Core/redux/slices/conversations.slice";
import { getThreadsMembers, manageMembers } from "../../../Core/redux/api/v3/conversationAPI";

// ── Same checkbox visual language as CreateGroupModal, plus a disabled
// (already-added) state: light grey fill/border instead of primary blue,
// grey checkmark instead of white.
const AddMembersCheckboxStyles = () => (
  <style>{`
    .wa-group-checkbox .ant-checkbox-inner {
      width: 18px !important;
      height: 18px !important;
      border-radius: 2px !important;
      border: 2px solid #667085 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      background-color: transparent !important;
    }
    .wa-group-checkbox .ant-checkbox-checked .ant-checkbox-inner {
      background-color: #165ABF !important;
      border-color: #165ABF !important;
    }
    .wa-group-checkbox .ant-checkbox-checked .ant-checkbox-inner::after {
      content: '' !important;
      position: static !important;
      border: none !important;
      transform: none !important;
      inset: auto !important;
      margin: 0 !important;
      width: 9px !important;
      height: 7px !important;
      background-image: url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='9'%20height='7'%20viewBox='0%200%2012%209.4'%3E%3Cpath%20d='M1%204.9L4.3%208.2L11%201'%20stroke='white'%20stroke-width='2'%20stroke-linecap='round'%20stroke-linejoin='round'%20fill='none'/%3E%3C/svg%3E") !important;
      background-repeat: no-repeat !important;
      background-position: center !important;
      background-size: 9px 7px !important;
    }
    .wa-group-checkbox:hover .ant-checkbox-inner {
      border-color: #165ABF !important;
    }
    .wa-group-checkbox .ant-checkbox-input:focus-visible + .ant-checkbox-inner {
      border-color: #165ABF !important;
    }
    /* Already-added / disabled state — grey instead of primary blue */
    .wa-group-checkbox.ant-checkbox-wrapper-disabled .ant-checkbox-checked .ant-checkbox-inner,
    .wa-group-checkbox .ant-checkbox-disabled.ant-checkbox-checked .ant-checkbox-inner {
      background-color: #EDF0F3 !important;
      border-color: #D0D5DD !important;
    }
    .wa-group-checkbox .ant-checkbox-disabled.ant-checkbox-checked .ant-checkbox-inner::after {
      background-image: url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='9'%20height='7'%20viewBox='0%200%2012%209.4'%3E%3Cpath%20d='M1%204.9L4.3%208.2L11%201'%20stroke='%2398A2B3'%20stroke-width='2'%20stroke-linecap='round'%20stroke-linejoin='round'%20fill='none'/%3E%3C/svg%3E") !important;
    }
  `}</style>
);

const AddMembersScrollbarStyles = () => (
  <style>{`
    .wa-group-scroll::-webkit-scrollbar { width: 8px; }
    .wa-group-scroll::-webkit-scrollbar-track { background: transparent; }
    .wa-group-scroll::-webkit-scrollbar-thumb {
      background-color: #D0D5DD;
      border-radius: 4px;
    }
    .wa-group-scroll::-webkit-scrollbar-thumb:hover { background-color: #98A2B3; }
    .wa-group-scroll { scrollbar-width: thin; scrollbar-color: #D0D5DD transparent; }
  `}</style>
);

const AddMembersSearchInputStyles = () => (
  <style>{`
    .wa-group-search-input:hover { border-color: #7DC7FF !important; }
    .wa-group-search-input.wa-group-search-focused {
      border-color: #7DC7FF !important;
      box-shadow: 0 0 0 2px #D1E9FF !important;
    }
  `}</style>
);

const AddMembersModalFadeStyles = () => (
  <style>{`
    @keyframes waAddMembersFadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes waAddMembersFadeOut { from { opacity: 1; } to { opacity: 0; } }

    .wa-add-members-modal .ant-zoom-enter,
    .wa-add-members-modal .ant-zoom-appear {
      opacity: 0 !important;
      transform: none !important;
    }
    .wa-add-members-modal .ant-zoom-enter.ant-zoom-enter-active,
    .wa-add-members-modal .ant-zoom-appear.ant-zoom-appear-active {
      animation-name: waAddMembersFadeIn !important;
      animation-duration: 0.2s !important;
      animation-timing-function: ease-out !important;
      transform: none !important;
    }
    .wa-add-members-modal .ant-zoom-leave.ant-zoom-leave-active {
      animation-name: waAddMembersFadeOut !important;
      animation-duration: 0.15s !important;
      animation-timing-function: ease-in !important;
    }
  `}</style>
);

// Same "List item" row as CreateGroupModal's ContactRow, extended with an
// "Already Added" subtitle + disabled state for existing members.
const AddMemberContactRow = ({ contact, checked, disabled, onCheck }) => (
  <div
    className={`flex items-center justify-between h-[56px] min-h-[56px] rounded-[8px] transition-colors -mx-2 px-2 ${disabled ? "cursor-default" : "cursor-pointer hover:bg-[#EEF3FB]"
      }`}
    onClick={() => !disabled && onCheck(contact.userId)}
  >
    <div className="flex items-center gap-x-3 min-w-0">
      <CustomAvatar
        title={`${contact.firstname} ${contact.lastname}`}
        src={contact.profilePicture}
        size={40}
        whiteText
        fontSize="16px"
        color={contact.profilePictureColorCode}
      />
      <div className="flex flex-col min-w-0">
        <span className="font-['Outfit'] font-semibold text-[16px] leading-[24px] text-[#292927] truncate">
          {contact.firstname} {contact.lastname}
        </span>
        {disabled && (
          <span className="font-['Outfit'] font-light text-[13px] leading-[18px] text-[#98A2B3]">
            Already Added
          </span>
        )}
      </div>
    </div>
    <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
      <Checkbox
        className="wa-group-checkbox"
        checked={checked}
        disabled={disabled}
        onChange={() => !disabled && onCheck(contact.userId)}
      />
    </div>
  </div>
);

const AddGroupMembersModal = () => {
  const dispatch = useAppDispatch();
  const { addMembersModal, threads, directMessageUsers, threadMembers } = useAppSelector(
    (state) => state.conversations
  );
  const { user } = useAppSelector((state) => state.userDetails);
  const { allowedUsersForGuest } = useAppSelector((state) => state.settings);

  const [selectedIds, setSelectedIds] = useState([]);
  const [searchFocused, setSearchFocused] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const visible = !!addMembersModal?.visible;
  const groupDetails = addMembersModal?.data || {};
  const threadId = groupDetails?.threadId;

  // ── Same contactPool merge as CreateGroupModal (DM-based, no tenantMembers
  // slice exists on web) ───────────────────────────────────────────────────
  const dmContactsFromThreads = useMemo(() => {
    return (threads || [])
      .filter((t) => t.isGroup === false)
      .map((t) => ({
        userId: t.userId ?? t.userid,
        firstname: t.firstname,
        lastname: t.lastname,
        profilePicture: t.profilePicture,
        profilePictureColorCode: t.profilePictureColorCode,
      }))
      .filter((c) => c.userId != null && +c.userId !== +user.id);
  }, [threads, user.id]);

  const contactPool = useMemo(() => {
    const byId = new Map();
    [...dmContactsFromThreads, ...(directMessageUsers || [])].forEach((c) => {
      if (c?.userId != null) {
        byId.set(String(c.userId), { ...byId.get(String(c.userId)), ...c });
      }
    });
    return Array.from(byId.values());
  }, [dmContactsFromThreads, directMessageUsers]);

  let contacts = contactPool;
  if (+user.roleId === 4) {
    contacts = contacts.filter((c) => allowedUsersForGuest?.includes(c.userId));
  }

  // Members already in the group — comes from state.conversations.threadMembers,
  // the same source GroupDetailsDrawer reads from for its member list.
  const existingMemberIds = useMemo(
    () => new Set((threadMembers || []).map((m) => String(m.userId ?? m.id))),
    [threadMembers]
  );

  const { search, setSearch, filteredData: rawFilteredContacts } = useSearch(contacts, [
    "firstname",
    "lastname",
  ]);

  // Letter-sectioned list, disabled (already-added) contacts bubbled to the
  // top of their own letter group, matching the screenshot ordering.
  const letterSections = useMemo(() => {
    const groups = {};
    (rawFilteredContacts || []).forEach((c) => {
      const letter = (c.firstname?.[0] || "#").toUpperCase();
      groups[letter] = groups[letter] || [];
      groups[letter].push(c);
    });
    return Object.keys(groups)
      .sort()
      .map((letter) => {
        const items = [...groups[letter]].sort((a, b) => {
          const aDisabled = existingMemberIds.has(String(a.userId)) ? 0 : 1;
          const bDisabled = existingMemberIds.has(String(b.userId)) ? 0 : 1;
          if (aDisabled !== bDisabled) return aDisabled - bDisabled;
          return (a.firstname || "").localeCompare(b.firstname || "");
        });
        return { letter, items };
      });
  }, [rawFilteredContacts, existingMemberIds]);

  const selectedContacts = contacts.filter((c) => selectedIds.includes(c.userId));

  const toggleContact = (id) => {
    if (existingMemberIds.has(String(id))) return; // guard, rows are already gated
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const reset = () => {
    setSelectedIds([]);
    setSearch("");
    setSearchFocused(false);
    setSubmitting(false);
    isSubmittingRef.current = false;
  };

  const onClose = () => {
    reset();
    dispatch(resetConversations(["addMembersModal"]));
  };

  const handleAddMembers = async () => {
    if (selectedIds.length === 0 || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setSubmitting(true);

    try {
      const response = await manageMembers({
        groupId: threadId,
        userIdToAdd: selectedContacts.map((c) => ({
          userId: c.userId,
          firstname: c.firstname,
          lastname: c.lastname,
        })),
        senderName: `${user.firstName} ${user.lastName}`.trim(),
      });

      if (response) {
        await dispatch(getThreadsMembers({ threadId }));
        onClose();
      } else {
        isSubmittingRef.current = false;
        setSubmitting(false);
      }
    } catch (e) {
      console.error("AddGroupMembersModal error:", e);
      isSubmittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <>
      <AddMembersCheckboxStyles />
      <AddMembersScrollbarStyles />
      <AddMembersModalFadeStyles />
      <AddMembersSearchInputStyles />
      <CustomModal
        visible={visible}
        width="376px"
        height="565px"
        maskClosable={false}
        onCancel={onClose}
        wrapClassName="wa-add-members-modal"
        body={
          <div
            className="p-4 flex flex-col gap-y-4 rounded-[12px]"
            style={{
              backgroundColor: "#F9FBFE",
              height: "565px",
              boxShadow:
                "0px 12px 16px -4px rgba(16,24,40,0.08), 0px 4px 6px -2px rgba(16,24,40,0.03)",
            }}
          >
            <h3 className="out-500-14 text-black !mb-0 !leading-4">Add members</h3>

            <div className="pb-4 -mx-4 px-4 border-b-[0.33px] border-grey/30">
              <CustomSearchBox
                search={search}
                setSearch={setSearch}
                placeholder="Search"
                className={`!bg-[#F0F5FA] wa-group-search-input ${searchFocused ? "wa-group-search-focused !border-[#7DC7FF]" : "!border-[#DDE5ED]"
                  }`}
                style={{ height: "35px" }}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
              />
            </div>

            <div className="wa-group-scroll max-h-[380px] flex-1 overflow-y-auto -mx-2 px-2">
              {contacts.length === 0 ? (
                <div className="py-8 text-center out-300-14 text-gray-500">
                  No contacts yet. Start a direct message with someone first, then you can add
                  them to this group.
                </div>
              ) : (
                letterSections.map((section, idx) => (
                  <div key={`${section.letter}-${idx}`}>
                    <div className="text-gray-500 out-500-12 text-[14px] px-2">
                      {section.letter}
                    </div>
                    {section.items.map((c) => (
                      <AddMemberContactRow
                        key={c.userId}
                        contact={c}
                        disabled={existingMemberIds.has(String(c.userId))}
                        checked={
                          existingMemberIds.has(String(c.userId)) || selectedIds.includes(c.userId)
                        }
                        onCheck={toggleContact}
                      />
                    ))}
                  </div>
                ))
              )}
            </div>

            <div className="flex justify-end gap-x-3 mt-auto pt-4">
              <CustomButton
                text="Cancel"
                className="bg-transparent text-gray-500"
                height="30px"
                width="64px"
                onClick={onClose}
              />
              <CustomButton
                text={submitting ? "Adding..." : "Add members"}
                height="30px"
                width="110px"
                disabled={selectedIds.length === 0 || submitting}
                onClick={handleAddMembers}
              />
            </div>
          </div>
        }
      />
    </>
  );
};

export default AddGroupMembersModal;